import { proxyCustomElement, HTMLElement, createEvent, h, Host, Fragment } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, e as emitEvent, l as logDeprecatedEventOnce } from './utils.js';
import { d as defineCustomElement$1 } from './cl-typography2.js';
import { c as createPopper } from './popper.js';

// Constants
const component = 'cl-tooltip';
const tooltipConstants = {
  POSITION: {
    AUTO: 'auto',
    BOTTOM: 'bottom',
    LEFT: 'left',
    RIGHT: 'right',
    TOP: 'top',
  },
};
// Type-validators
const tooltipValidator = {
  position: createTypeValidator(tooltipConstants.POSITION),
};

const clTooltipCss = ".sc-cl-tooltip-h{--tooltip-delay:var(--animation-duration-fast);--tooltip-z-index:var(--z-index-1);--tooltip-label-color:var(--font-color-onlight-default);--tooltip-max-width:80vw;color:rgb(var(--tooltip-label-color));outline:none}.sc-cl-tooltip-h,.sc-cl-tooltip-h *.sc-cl-tooltip,.sc-cl-tooltip-h *.sc-cl-tooltip::before,.sc-cl-tooltip-h *.sc-cl-tooltip::after,.sc-cl-tooltip-h .sc-cl-tooltip-s>*,.sc-cl-tooltip-h .sc-cl-tooltip-s *,.sc-cl-tooltip-h .sc-cl-tooltip-s *::before,.sc-cl-tooltip-h .sc-cl-tooltip-s *::after{box-sizing:border-box}.sc-cl-tooltip-h .sc-cl-tooltip-s>*{--typography-color:rgb(var(--tooltip-label-color));--typography-size:var(--font-size-microcopy);line-height:100%}.sc-cl-tooltip-h .tooltip.sc-cl-tooltip{background-color:rgb(var(--color-neutral-main));border:transparent;border-radius:var(--border-radius-interactive-small);box-shadow:var(--elevation-2);display:none;max-width:var(--tooltip-max-width);z-index:var(--tooltip-z-index)}.sc-cl-tooltip-h .tooltip[data-show].sc-cl-tooltip{display:block}.sc-cl-tooltip-h .tooltip__label.sc-cl-tooltip{align-items:center;color:rgb(var(--tooltip-label-color));display:flex;font-family:var(--font-family-ui);font-size:var(--font-size-microcopy);padding:var(--spacing-half) var(--spacing-2);position:relative;width:100%}.sc-cl-tooltip-h .tooltip__label.sc-cl-tooltip cl-typography.sc-cl-tooltip{--typography-color:rgb(var(--tooltip-label-color));font-size:var(--typography-size, var(--font-size-microcopy));line-height:100%}.sc-cl-tooltip-h .tooltip__arrow.sc-cl-tooltip,.sc-cl-tooltip-h .tooltip__arrow.sc-cl-tooltip::before{background:inherit;height:var(--spacing-1);position:absolute;width:var(--spacing-1)}.sc-cl-tooltip-h .tooltip__arrow.sc-cl-tooltip{visibility:hidden}.sc-cl-tooltip-h .tooltip__arrow.sc-cl-tooltip::before{content:\"\";transform:rotate(45deg);visibility:visible}.sc-cl-tooltip-h .tooltip__arrow--hidden.sc-cl-tooltip::before{visibility:hidden}.sc-cl-tooltip-h .tooltip--dark.sc-cl-tooltip{--tooltip-label-color:var(--font-color-onbrand-default);background-color:rgba(var(--color-neutral-600))}.sc-cl-tooltip-h .tooltip--open.sc-cl-tooltip{display:block}.sc-cl-tooltip-h .tooltip[data-popper-placement^=top].sc-cl-tooltip>.tooltip__arrow.sc-cl-tooltip{bottom:calc(-1 * var(--spacing-half))}.sc-cl-tooltip-h .tooltip[data-popper-placement^=bottom].sc-cl-tooltip>.tooltip__arrow.sc-cl-tooltip{top:calc(-1 * var(--spacing-half))}.sc-cl-tooltip-h .tooltip[data-popper-placement^=left].sc-cl-tooltip>.tooltip__arrow.sc-cl-tooltip{right:calc(-1 * (var(--spacing-quarter) + var(--spacing-eighth) / 2))}.sc-cl-tooltip-h .tooltip[data-popper-placement^=right].sc-cl-tooltip>.tooltip__arrow.sc-cl-tooltip{left:calc(-1 * (var(--spacing-quarter) + var(--spacing-eighth) / 2))}";

const ClTooltip = /*@__PURE__*/ proxyCustomElement(class ClTooltip extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clInitEvent = createEvent(this, "clInit", 7);
    this.initEvent = createEvent(this, "init", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Events that should trigger showing the tooltip
     */
    this.showEvents = ['mouseenter', 'focus'];
    /**
     * Events that should trigger hiding the tooltip
     */
    this.hideEvents = ['mouseleave', 'blur'];
    /**
     * Constructor for the tooltip
     */
    this.initialiseTooltip = () => {
      if (this.tooltipElement) {
        this.popperInstance = createPopper(this.referenceElement, this.tooltipElement, {
          placement: this.position,
          strategy: 'fixed',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
          onFirstUpdate: () => {
            emitEvent(this, 'clInitEvent', { component }, null);
          },
        });
        this.handleForceUpdate();
      }
    };
    /**
     * Adds data-show attribute to make the tooltip visible
     */
    this.show = () => {
      if (this.tooltipElement && this.label) {
        this.tooltipElement.setAttribute('data-show', '');
        this.handleForceUpdate();
      }
    };
    /**
     * Removes data-show attribute to hide the tooltip visibility
     */
    this.hide = () => {
      if (this.tooltipElement && this.label) {
        this.tooltipElement.removeAttribute('data-show');
        this.handleForceUpdate();
      }
    };
    this.referenceElement = undefined;
    this.arrow = false;
    this.dark = false;
    this.label = '';
    this.open = false;
    this.position = 'auto';
  }
  /**
   * Validates the position property
   */
  handlePosition(position) {
    if (!tooltipValidator.position(position)) {
      this.logger.error.attribute.position(`Given invalid value "${position}".`);
    }
    if (this.popperInstance) {
      this.popperInstance.setOptions({
        placement: position,
      });
      this.popperInstance.forceUpdate();
    }
  }
  /**
   * Function that handles force-updating the popper instance when a property changes
   */
  handleForceUpdate() {
    if (this.popperInstance) {
      this.popperInstance.forceUpdate();
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Validate the position attribute;
   * - Attach hide and show eventListeners
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'init', 'clInit');
    this.handlePosition(this.position);
    this.showEvents.forEach(event => {
      this.element.addEventListener(event, this.show);
    });
    this.hideEvents.forEach(event => {
      this.element.addEventListener(event, this.hide);
    });
  }
  /**
   * Every time the component is disconnected from the DOM:
   * - Remove the hide and show eventListeners
   * - Remove the popper instance
   */
  disconnectedCallback() {
    var _a;
    this.showEvents.forEach(event => {
      this.element.removeEventListener(event, this.show);
    });
    this.hideEvents.forEach(event => {
      this.element.removeEventListener(event, this.hide);
    });
    (_a = this.popperInstance) === null || _a === void 0 ? void 0 : _a.destroy();
  }
  /**
   * After every render:
   * - Set the reference to the nested element;
   * - Initialise the popper tooltip element;
   */
  componentDidRender() {
    this.referenceElement = this.element.children[0];
    this.initialiseTooltip();
  }
  render() {
    const { arrow, dark, label, open } = this;
    return (h(Host, { tabIndex: -1 }, label && (h(Fragment, null, h("slot", null), h("div", { ref: el => {
        this.tooltipElement = el;
      }, class: cl('tooltip', {
        'tooltip--dark': dark,
        'tooltip--open': open,
      }), role: 'tooltip' }, h("div", { class: 'tooltip__label' }, h("slot", { name: 'label' }, h("cl-typography", { variant: 'microcopy' }, label))), h("div", { class: cl('tooltip__arrow', { ['tooltip__arrow--hidden']: !arrow }), "data-popper-arrow": true }))))));
  }
  get element() { return this; }
  static get watchers() { return {
    "position": ["handlePosition"],
    "dark": ["handleForceUpdate"],
    "label": ["handleForceUpdate"],
    "open": ["handleForceUpdate"],
    "arrow": ["handleForceUpdate"]
  }; }
  static get style() { return clTooltipCss; }
}, [6, "cl-tooltip", {
    "arrow": [4],
    "dark": [4],
    "label": [1],
    "open": [4],
    "position": [1],
    "referenceElement": [32]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-tooltip", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-tooltip":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClTooltip);
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { ClTooltip as C, defineCustomElement as d };

//# sourceMappingURL=cl-tooltip2.js.map